import MarkerClusterer from 'marker-clusterer-plus-es2015';
import {noop} from '../common/common.utils';

const MAX_AUTOZOOM = 18;

/** Logic and simple wrapper for the Location Google Maps */
export class LocationMap {
  constructor($q, $timeout, $rootScope, $compile, GaMaps, infoWindowCallback=noop, map_options={}) {

    this.$q = $q;
    this.$timeout = $timeout;
    this.$compile = $compile;
    this.GaMaps = GaMaps;

    /*eslint no-unused-vars: "off"*/
    /**
     * This callback gets called when user selects a location from the map. Timeout used to ensure we are inside a
     * digest loop.
     */
    this._infoWindowCallback = id => $timeout(() => infoWindowCallback(id));

    this.infoWindow = null;

    this.map = null;

    this.googleMaps = null;

    this.markerClusterer = null;

    this.mapProperties = {
        center: {
          lat: 64.0,
          lng: 26.0
        },

        options: {
          scrollwheel: false,
          disableDoubleClickZoom: true,
          animatedZoom: false
        }
    };
    Object.assign(this.mapProperties, map_options);

    this.scope = $rootScope.$new(true);
    this.scope.addSelectButton = false;
    this.scope.useCustomSelectButtonText = false;
  }

  /**
   * Initialize The Google Maps view.
   * @param {String} id The HTML element ID to attach the map to.
   * @returns {Promise} Promise that will be resolved when map is loaded.
   */
  initialize(id='locations-map') {
    return this.$q(resolve => {
      this.GaMaps.map().then(maps => {
        this.googleMaps = maps;
        this.$timeout(() => {
          this.map = new maps.Map(document.getElementById(id), this.mapProperties);
          this.infoWindow = new this.googleMaps.InfoWindow();
          this.markerClusterer = new MarkerClusterer(this.map, [], {
            imagePath: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m'});
          resolve();
        });
      });
    });
  }

  /**
   * Update markers according to the given locations.
   * @param {Array.<Location>} locations
   * @param {boolean} add_select_button Set to true to add a "select this location" button in the popup when location
   *   pin is clicked.
   */
  updateMarkers(locations, add_select_button=true, selectButtonCustomText=null, centerToLocationsAmount=null) {
    if (this.markerClusterer == null) {
      // Too early update call, can't process
      return;
    }
    let bounds = new this.googleMaps.LatLngBounds();

    // MarkerClusterer "owns" the markers and removes them from the map
    this.markerClusterer.clearMarkers();

    let boundsExtendedCount = 0;

    for (const location of locations) {
      if (location.isArea()) {
        // Filter out area types from map
        continue;
      }
      if (location.address.coordinates.lat && location.address.coordinates.lon) {
        const position = {lat: location.address.coordinates.lat, lng: location.address.coordinates.lon};
        const marker = new this.googleMaps.Marker({
          map: this.map,
          position: position,
          oralLocation: location // Custom property used to link the marker to a location
        });

        marker.addListener('click', () => {
          const markerContent = this.buildInfoWindowContent(marker.oralLocation, add_select_button, selectButtonCustomText);
          this.$timeout(() => {
            this.infoWindow.setContent(markerContent[0]);
            this.infoWindow.open(this.map, marker);
          });
        });

        this.markerClusterer.addMarker(marker);

        if (centerToLocationsAmount == null || centerToLocationsAmount != null && boundsExtendedCount < centerToLocationsAmount) {
          bounds.extend(position);
          boundsExtendedCount += 1;
        }
      }
    }

    // Autofit bounds if many locations, set fixed zoom otherwise
    if (!locations.length) {
      this.map.setCenter(this.mapProperties.center);
      this.map.setZoom(5);
    } else if (locations.length === 1) {
      this.map.setCenter({lat: locations[0].address.coordinates.lat, lng: locations[0].address.coordinates.lon});
      this.map.setZoom(15);
    } else {
      this.map.fitBounds(bounds);
      // Don't zoom in too close when there are few locations very close to each other.
      if (this.map.zoom > MAX_AUTOZOOM) {
        this.map.setZoom(MAX_AUTOZOOM);
      }
    }

  }

  /**
   * Build HTML for the info window of a location marker.
   * @param {Location} location
   * @param {boolean} add_select_button Set to true to add a "select this location" button in the popup when location
   *   pin is clicked.
   */
  buildInfoWindowContent(location, add_select_button=true, selectButtonCustomText=null) {
    this.scope.addSelectButton = add_select_button;
    this.scope.useCustomSelectButtonText = Boolean(selectButtonCustomText);
    this.scope.onSelectLocation = id => this._infoWindowCallback(id);

    const element = angular.element(`
      <div class="oral-info-window-content">
        <p class="oral-info-window-header">${location.name}</p>
        <p class="oral-info-window-address">${location.address.street}, ${location.address.postal_code} ${location.address.postal_area}</p>
        <norm-button ng-if="addSelectButton" block="true" secondary="true" click-action="onSelectLocation(${location.id})">
          <span ng-if="useCustomSelectButtonText">
            ${selectButtonCustomText}
          </span>
          <span ng-if="!useCustomSelectButtonText">
            {{ 'Valitse toimipiste' | translate }}
          </span>
        </norm-button>
      </div>
    `);

    return this.$compile(element)(this.scope);
  }
}



// WEBPACK FOOTER //
// ./src/app/locations/location-map.js